// ─────────────────────────────────────────────────────────────
// Synastry Calculation Service, client-side astrological compatibility
// ─────────────────────────────────────────────────────────────

import {
  calculatePlanetPositions,
  getEclipticLongitude,
  longitudeToSign,
  longitudeToHouse,
  solarHouseCusp,
  PLANET_NAMES_PT,
  PLANET_GLYPHS,
  PLANETS,
} from './ephemeris';
import type { Planet } from './ephemeris';

// ─── Types ───────────────────────────────────────────────────

export interface SynPlanet {
  key: string;        // 'Sun', 'Moon', etc.
  namePt: string;     // 'Sol', 'Lua', etc.
  symbol: string;
  sign: string;
  degree: number;
  longitude: number;
  house: number;
  isRetrograde: boolean;
}

export interface SynChart {
  planets: SynPlanet[];
  solarCusp0: number;  // start longitude of Sun's sign
  mcLon: number | null;
}

export interface InterAspect {
  planet1: string;   // namePt of person A's planet
  planet2: string;   // namePt of person B's planet
  key1: string;      // 'Sun', 'Moon', etc.
  key2: string;
  aspect: string;    // 'conjunction'|'sextile'|'square'|'trine'|'opposition'
  aspectPt: string;  // 'Conjunção'|'Sextil'|'Quadratura'|'Trígono'|'Oposição'
  orb: number;
  quality: 'harmonious' | 'tense' | 'neutral';
  lon1: number;
  lon2: number;
}

export interface ElementCounts { fire: number; earth: number; air: number; water: number; }
export interface ModalityCounts { cardinal: number; fixed: number; mutable: number; }

export interface ActivatedHouse {
  house: number;
  theme: string;
  planets: string[];  // namePt values of activating planets
}

export interface SynProgressions {
  sun: { sign: string; degree: number; longitude: number };
  moon: { sign: string; degree: number; longitude: number };
  venus: { sign: string; degree: number; longitude: number };
}

export interface CompatibilityScores {
  love: number;
  communication: number;
  values: number;
  longTerm: number;
  growth: number;
  routine: number;
  family: number;
}

export interface FullSynastryData {
  chartA: SynChart;
  chartB: SynChart;
  aspects: InterAspect[];          // all inter-chart aspects sorted by orb
  composite: SynChart;             // midpoint composite
  davison: SynChart;               // Davison chart
  davisonDate: Date;
  currentSky: SynPlanet[];         // today's planets
  progressionsA: SynProgressions;  // secondary progressions for person A
  progressionsB: SynProgressions;
  elementsA: ElementCounts;
  modalitiesA: ModalityCounts;
  elementsB: ElementCounts;
  modalitiesB: ModalityCounts;
  housesActivatedInA: ActivatedHouse[];  // B's planets in A's houses
  housesActivatedInB: ActivatedHouse[];  // A's planets in B's houses
  scores: CompatibilityScores;
}

// ─── Constants ───────────────────────────────────────────────

const ASPECT_DEFS = [
  { name: 'conjunction', angle: 0,   orb: 8, quality: 'neutral'    as const, namePt: 'Conjunção' },
  { name: 'sextile',     angle: 60,  orb: 6, quality: 'harmonious' as const, namePt: 'Sextil' },
  { name: 'square',      angle: 90,  orb: 8, quality: 'tense'      as const, namePt: 'Quadratura' },
  { name: 'trine',       angle: 120, orb: 8, quality: 'harmonious' as const, namePt: 'Trígono' },
  { name: 'opposition',  angle: 180, orb: 8, quality: 'tense'      as const, namePt: 'Oposição' },
];

const SIGN_ELEMENT: Record<string, keyof ElementCounts> = {
  'Áries': 'fire', 'Leão': 'fire', 'Sagitário': 'fire',
  'Touro': 'earth', 'Virgem': 'earth', 'Capricórnio': 'earth',
  'Gêmeos': 'air', 'Libra': 'air', 'Aquário': 'air',
  'Câncer': 'water', 'Escorpião': 'water', 'Peixes': 'water',
};

const SIGN_MODALITY: Record<string, keyof ModalityCounts> = {
  'Áries': 'cardinal', 'Câncer': 'cardinal', 'Libra': 'cardinal', 'Capricórnio': 'cardinal',
  'Touro': 'fixed', 'Leão': 'fixed', 'Escorpião': 'fixed', 'Aquário': 'fixed',
  'Gêmeos': 'mutable', 'Virgem': 'mutable', 'Sagitário': 'mutable', 'Peixes': 'mutable',
};

const HOUSE_THEMES: Record<number, string> = {
  1: 'Identidade e Aparência',
  2: 'Recursos e Valores',
  3: 'Comunicação',
  4: 'Lar e Família',
  5: 'Amor e Criatividade',
  6: 'Rotina e Saúde',
  7: 'Parcerias',
  8: 'Intimidade e Transformação',
  9: 'Expansão e Filosofia',
  10: 'Carreira',
  11: 'Amizades',
  12: 'Espiritualidade',
};

// ─── Helper ───────────────────────────────────────────────────

function mod360(x: number): number {
  return ((x % 360) + 360) % 360;
}

// ─── Build SynChart ───────────────────────────────────────────

export function buildSynChart(date: Date, mcLon?: number | null): SynChart {
  const positions = calculatePlanetPositions(date);
  const sunPos = positions.find(p => p.planet === 'Sun')!;
  const solarCusp0 = solarHouseCusp(sunPos.longitude);

  const planets: SynPlanet[] = positions.map(p => ({
    key: p.planet as string,
    namePt: PLANET_NAMES_PT[p.planet as Planet],
    symbol: PLANET_GLYPHS[p.planet as Planet],
    sign: p.sign,
    degree: p.degree,
    longitude: p.longitude,
    house: p.house,
    isRetrograde: p.isRetrograde,
  }));

  return {
    planets,
    solarCusp0,
    mcLon: mcLon ?? null,
  };
}

// ─── Inter-chart Aspects ──────────────────────────────────────

export function calcInterAspects(planetsA: SynPlanet[], planetsB: SynPlanet[]): InterAspect[] {
  const aspects: InterAspect[] = [];

  for (const pA of planetsA) {
    for (const pB of planetsB) {
      const diff = Math.abs(pA.longitude - pB.longitude);
      const angle = diff > 180 ? 360 - diff : diff;

      for (const def of ASPECT_DEFS) {
        const orb = Math.abs(angle - def.angle);
        if (orb <= def.orb) {
          aspects.push({
            planet1: pA.namePt,
            planet2: pB.namePt,
            key1: pA.key,
            key2: pB.key,
            aspect: def.name,
            aspectPt: def.namePt,
            orb: parseFloat(orb.toFixed(2)),
            quality: def.quality,
            lon1: pA.longitude,
            lon2: pB.longitude,
          });
          break;
        }
      }
    }
  }

  return aspects.sort((a, b) => a.orb - b.orb);
}

// ─── Composite Chart (midpoint method) ───────────────────────

export function calcComposite(chartA: SynChart, chartB: SynChart): SynChart {
  const planetMap = new Map<string, SynPlanet>();
  for (const pA of chartA.planets) {
    const pB = chartB.planets.find(p => p.key === pA.key);
    if (!pB) continue;

    const lon1 = pA.longitude;
    const lon2 = pB.longitude;
    let mid: number;
    if (Math.abs(lon2 - lon1) <= 180) {
      mid = (lon1 + lon2) / 2;
    } else {
      mid = mod360((lon1 + lon2) / 2 + 180);
    }
    mid = mod360(mid);

    const signInfo = longitudeToSign(mid);
    planetMap.set(pA.key, {
      key: pA.key,
      namePt: pA.namePt,
      symbol: pA.symbol,
      sign: signInfo.sign,
      degree: signInfo.degree,
      longitude: mid,
      house: 0, // will be set below
      isRetrograde: false,
    });
  }

  // Get composite Sun longitude for solar cusp
  const compositeSun = planetMap.get('Sun');
  const solarCusp0 = compositeSun ? solarHouseCusp(compositeSun.longitude) : 0;

  // Assign houses
  const planets: SynPlanet[] = Array.from(planetMap.values()).map(p => ({
    ...p,
    house: longitudeToHouse(p.longitude, solarCusp0),
  }));

  return { planets, solarCusp0, mcLon: null };
}

// ─── Davison Chart ────────────────────────────────────────────

export function calcDavison(dateA: Date, dateB: Date): { chart: SynChart; davisonDate: Date } {
  const jdA = dateA.getTime() / 86400000 + 2440587.5;
  const jdB = dateB.getTime() / 86400000 + 2440587.5;
  const avgJd = (jdA + jdB) / 2;
  const davisonDate = new Date((avgJd - 2440587.5) * 86400000);
  const chart = buildSynChart(davisonDate);
  return { chart, davisonDate };
}

// ─── Secondary Progressions ───────────────────────────────────

export function calcProgressions(birthDate: Date, targetDate: Date): SynProgressions {
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
  const yearsElapsed = (targetDate.getTime() - birthDate.getTime()) / msPerYear;
  // 1 day = 1 year
  const progressedDate = new Date(birthDate.getTime() + yearsElapsed * 86400000);

  const sunLon = getEclipticLongitude('Sun', progressedDate);
  const moonLon = getEclipticLongitude('Moon', progressedDate);
  const venusLon = getEclipticLongitude('Venus', progressedDate);

  const sunInfo = longitudeToSign(sunLon);
  const moonInfo = longitudeToSign(moonLon);
  const venusInfo = longitudeToSign(venusLon);

  return {
    sun:   { sign: sunInfo.sign,   degree: sunInfo.degree,   longitude: sunLon },
    moon:  { sign: moonInfo.sign,  degree: moonInfo.degree,  longitude: moonLon },
    venus: { sign: venusInfo.sign, degree: venusInfo.degree, longitude: venusLon },
  };
}

// ─── Element & Modality Counts ────────────────────────────────

export function calcElements(planets: SynPlanet[]): ElementCounts {
  const counts: ElementCounts = { fire: 0, earth: 0, air: 0, water: 0 };
  for (const p of planets) {
    const el = SIGN_ELEMENT[p.sign];
    if (el) counts[el]++;
  }
  return counts;
}

export function calcModalities(planets: SynPlanet[]): ModalityCounts {
  const counts: ModalityCounts = { cardinal: 0, fixed: 0, mutable: 0 };
  for (const p of planets) {
    const mod = SIGN_MODALITY[p.sign];
    if (mod) counts[mod]++;
  }
  return counts;
}

// ─── Houses Activated ─────────────────────────────────────────

export function calcHousesActivated(planetsOther: SynPlanet[], chartSelf: SynChart): ActivatedHouse[] {
  const houseMap = new Map<number, string[]>();
  // Initialize all 12 houses
  for (let i = 1; i <= 12; i++) {
    houseMap.set(i, []);
  }

  for (const p of planetsOther) {
    const house = longitudeToHouse(p.longitude, chartSelf.solarCusp0);
    houseMap.get(house)!.push(p.namePt);
  }

  return Array.from(houseMap.entries()).map(([house, planets]) => ({
    house,
    theme: HOUSE_THEMES[house] ?? `Casa ${house}`,
    planets,
  }));
}

// ─── Compatibility Scores ─────────────────────────────────────

function clamp(val: number, min = 20, max = 98): number {
  return Math.max(min, Math.min(max, val));
}

export function calcCompatibilityScores(aspects: InterAspect[]): CompatibilityScores {
  let love = 50;
  let communication = 50;
  let values = 50;
  let longTerm = 50;
  let growth = 50;
  let routine = 50;
  let family = 50;

  for (const asp of aspects) {
    const k1 = asp.key1.toLowerCase();
    const k2 = asp.key2.toLowerCase();
    const q = asp.quality;
    const a = asp.aspect;

    const isVenMars = (k1 === 'venus' && k2 === 'mars') || (k1 === 'mars' && k2 === 'venus');
    const isVenVen = k1 === 'venus' && k2 === 'venus';
    const isSunMoon = (k1 === 'sun' && k2 === 'moon') || (k1 === 'moon' && k2 === 'sun');
    const isMerMer = k1 === 'mercury' && k2 === 'mercury';
    const isMerSunMoon = (k1 === 'mercury' && (k2 === 'sun' || k2 === 'moon')) || ((k1 === 'sun' || k1 === 'moon') && k2 === 'mercury');
    const isSaturn = k1 === 'saturn' || k2 === 'saturn';
    const isJupiter = k1 === 'jupiter' || k2 === 'jupiter';
    const isUrNep = k1 === 'uranus' || k2 === 'uranus' || k1 === 'neptune' || k2 === 'neptune';
    const isMoonMoon = k1 === 'moon' && k2 === 'moon';
    const isMoonSat = (k1 === 'moon' && k2 === 'saturn') || (k1 === 'saturn' && k2 === 'moon');
    const isMoonMars = (k1 === 'moon' && k2 === 'mars') || (k1 === 'mars' && k2 === 'moon');
    const isSunSun = k1 === 'sun' && k2 === 'sun';
    const isMoonLua = k1 === 'moon' || k2 === 'moon';
    const isVenMoon = (k1 === 'venus' && k2 === 'moon') || (k1 === 'moon' && k2 === 'venus');

    // Love
    if (isVenMars) {
      if (a === 'conjunction') love += 8;
      else if (q === 'harmonious') love += 6;
      else love += 3;
    }
    if (isVenVen) {
      if (q === 'harmonious') love += (a === 'trine' ? 5 : 4);
      else if (q === 'tense') love -= 2;
    }
    if (isSunMoon) {
      if (q === 'harmonious') love += 5;
      else if (q === 'tense') love -= 1;
      if (a === 'conjunction') love += 4;
    }

    // Communication
    if (isMerMer) {
      if (q === 'harmonious') communication += 7;
      else if (q === 'tense') communication -= 3;
      if (a === 'conjunction') communication += 5;
    }
    if (isMerSunMoon) {
      if (q === 'harmonious') communication += 4;
      else if (q === 'tense') communication -= 2;
    }

    // Values
    if (isVenVen) {
      if (q === 'harmonious') values += 6;
    }
    if (isMoonMoon) {
      if (q === 'harmonious') values += 5;
      else if (q === 'tense') values -= 2;
    }
    if (isSunSun) {
      if (q === 'harmonious') values += 4;
    }

    // Long term
    if (isSaturn) {
      if (q === 'harmonious') longTerm += 5;
      else if (q === 'tense') longTerm -= 3;
      if (a === 'conjunction') longTerm += 2;
    }
    if (isMoonSat && q === 'harmonious') longTerm += 4;

    // Growth
    if (isJupiter) {
      if (q === 'harmonious') growth += 6;
      else if (q === 'tense') growth -= 1;
    }
    if (isUrNep && q === 'harmonious') growth += 3;

    // Routine
    if (isMoonMoon) {
      if (q === 'harmonious') routine += 6;
      else if (q === 'tense') routine -= 3;
    }
    if (isMoonSat) {
      if (q === 'harmonious') routine += 4;
      else if (q === 'tense') routine -= 4;
    }
    if (isMoonMars) {
      if (q === 'harmonious') routine += 2;
      else if (q === 'tense') routine -= 4;
    }

    // Family
    if (isMoonMoon) {
      if (q === 'harmonious') family += 8;
      else if (q === 'tense') family -= 4;
    }
    if (isSunMoon && q === 'harmonious') family += 5;
    if (isVenMoon && q === 'harmonious') family += 4;
    // suppress unused warning
    void isMoonLua;
  }

  return {
    love:          clamp(Math.round(love)),
    communication: clamp(Math.round(communication)),
    values:        clamp(Math.round(values)),
    longTerm:      clamp(Math.round(longTerm)),
    growth:        clamp(Math.round(growth)),
    routine:       clamp(Math.round(routine)),
    family:        clamp(Math.round(family)),
  };
}

// ─── Master function ──────────────────────────────────────────

export function calculateFullSynastry(
  dateA: Date,
  dateB: Date,
  mcLonA?: number | null,
  mcLonB?: number | null,
): FullSynastryData {
  const chartA = buildSynChart(dateA, mcLonA);
  const chartB = buildSynChart(dateB, mcLonB);

  const aspects = calcInterAspects(chartA.planets, chartB.planets);
  const composite = calcComposite(chartA, chartB);
  const { chart: davison, davisonDate } = calcDavison(dateA, dateB);

  const today = new Date();
  const currentSkyPositions = calculatePlanetPositions(today);
  const currentSky: SynPlanet[] = currentSkyPositions.map(p => ({
    key: p.planet as string,
    namePt: PLANET_NAMES_PT[p.planet as Planet],
    symbol: PLANET_GLYPHS[p.planet as Planet],
    sign: p.sign,
    degree: p.degree,
    longitude: p.longitude,
    house: p.house,
    isRetrograde: p.isRetrograde,
  }));

  const progressionsA = calcProgressions(dateA, today);
  const progressionsB = calcProgressions(dateB, today);

  const elementsA = calcElements(chartA.planets);
  const modalitiesA = calcModalities(chartA.planets);
  const elementsB = calcElements(chartB.planets);
  const modalitiesB = calcModalities(chartB.planets);

  const housesActivatedInA = calcHousesActivated(chartB.planets, chartA);
  const housesActivatedInB = calcHousesActivated(chartA.planets, chartB);

  const scores = calcCompatibilityScores(aspects);

  return {
    chartA,
    chartB,
    aspects,
    composite,
    davison,
    davisonDate,
    currentSky,
    progressionsA,
    progressionsB,
    elementsA,
    modalitiesA,
    elementsB,
    modalitiesB,
    housesActivatedInA,
    housesActivatedInB,
    scores,
  };
}

// Re-export PLANETS for use in other files
export { PLANETS };
